import type { Experiment, Variant } from 'types';
import type { VariantFieldsUpdate } from '@/state/experiments/useVariants';
import { useState } from 'react';
import { FLAG_KEY_PATTERN, MAX_VARIANT_PAYLOAD_BYTES } from 'definitions';
import { cn } from 'helpers/cn';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useVariants } from '@/state/experiments/useVariants';
import { apiErrorMessage } from '@/state/api-error';
import { isPreStart } from 'helpers/experiment-status';

type EditVariantPayloadModalProps = {
  variant: Variant;
  experiment: Experiment;
  open: boolean;
  onClose: () => void;
};

/** The payload text read as JSON: empty clears it, anything else must parse. */
export type PayloadParse = { ok: true; value: unknown; bytes: number } | { ok: false; reason: string };

export const EDIT_VARIANT_COPY = {
  title: 'Key and payload',
  keyLabel: 'Variant key',
  keyHelp: 'What your code switches on. Lowercase letters, digits and hyphens.',
  keyLocked: 'Set while the experiment is a draft: your code switches on it.',
  keyInvalid: 'Use lowercase letters, digits and hyphens, starting with a letter or digit.',
  payloadLabel: 'Payload (JSON)',
  payloadHelp: 'Optional configuration the SDK returns with this variant. It can change while the experiment runs.',
  payloadEmpty: 'No payload.',
} as const;

const FIELD_CLASS = 'border-line bg-field text-foreground rounded-lg';

/** Measured the way the API measures it: the UTF-8 length of the compact JSON. */
export function parsePayload(text: string): PayloadParse {
  if (!text.trim()) return { ok: true, value: null, bytes: 0 };
  try {
    const value: unknown = JSON.parse(text);
    const bytes = new TextEncoder().encode(JSON.stringify(value)).length;
    if (bytes > MAX_VARIANT_PAYLOAD_BYTES) {
      return { ok: false, reason: `Too large: ${bytes} of ${MAX_VARIANT_PAYLOAD_BYTES} bytes.` };
    }
    return { ok: true, value, bytes };
  } catch (error) {
    return { ok: false, reason: `Not valid JSON: ${error instanceof Error ? error.message : 'parse error'}` };
  }
}

/**
 * A Server-side experiment variant's SDK fields, in the EditVariantModal shape. The key is locked
 * outside Draft (the API answers 409); the payload is JSON, parsed as it is typed, with its
 * size shown against the 8 KB cap.
 */
export const EditVariantPayloadModal = ({ variant, experiment, open, onClose }: EditVariantPayloadModalProps) => {
  const { updateVariantFields } = useVariants(experiment.id);
  const [key, setKey] = useState(variant.key ?? '');
  const [payloadText, setPayloadText] = useState(
    variant.payload == null ? '' : JSON.stringify(variant.payload, null, 2),
  );
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const isDraft = isPreStart(experiment.status);
  const trimmedKey = key.trim();
  const keyError = trimmedKey && !FLAG_KEY_PATTERN.test(trimmedKey) ? EDIT_VARIANT_COPY.keyInvalid : null;
  const payload = parsePayload(payloadText);

  const nextKey = trimmedKey || null;
  const keyChanged = isDraft && nextKey !== (variant.key ?? null);
  const payloadChanged = payload.ok && JSON.stringify(payload.value) !== JSON.stringify(variant.payload ?? null);
  const canSave = !isSaving && !keyError && payload.ok && (keyChanged || payloadChanged);

  const onSave = async () => {
    if (!canSave || !payload.ok) return;
    const fields: VariantFieldsUpdate = {};
    if (keyChanged) fields.key = nextKey;
    if (payloadChanged) fields.payload = payload.value;
    setIsSaving(true);
    setSaveError(null);
    try {
      await updateVariantFields(variant.id, fields);
      onClose();
    } catch (error) {
      // Kept in the dialog: a duplicate key (409) is fixed by editing what is still on screen.
      setSaveError(apiErrorMessage(error, 'Could not save the variant.'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="border-line bg-panel sm:rounded-panel sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="text-title font-medium">
            {EDIT_VARIANT_COPY.title}: {variant.name}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-2">
          <div className="flex flex-col gap-2">
            <Label htmlFor="variant-key" className="micro-label">
              {EDIT_VARIANT_COPY.keyLabel}
            </Label>
            <Input
              id="variant-key"
              value={key}
              onChange={(ev) => setKey(ev.target.value)}
              disabled={!isDraft}
              placeholder="control"
              aria-invalid={!!keyError}
              className={cn(FIELD_CLASS, 'font-mono')}
            />
            <p className={cn('supporting m-0', keyError && 'text-danger')}>
              {keyError ?? (isDraft ? EDIT_VARIANT_COPY.keyHelp : EDIT_VARIANT_COPY.keyLocked)}
            </p>
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="variant-payload" className="micro-label">
              {EDIT_VARIANT_COPY.payloadLabel}
            </Label>
            <Textarea
              id="variant-payload"
              value={payloadText}
              onChange={(ev) => setPayloadText(ev.target.value)}
              spellCheck={false}
              placeholder={'{ "headline": "Free shipping today" }'}
              aria-invalid={!payload.ok}
              className={cn(FIELD_CLASS, 'min-h-40 resize-y font-mono text-micro')}
            />
            <div className="flex items-start justify-between gap-3">
              <p className={cn('supporting m-0', !payload.ok && 'text-danger')}>
                {payload.ok ? EDIT_VARIANT_COPY.payloadHelp : payload.reason}
              </p>
              <span className={cn('num text-dim shrink-0', !payload.ok && 'text-danger')}>
                {payload.ok
                  ? payload.value === null
                    ? EDIT_VARIANT_COPY.payloadEmpty
                    : `${payload.bytes} / ${MAX_VARIANT_PAYLOAD_BYTES} bytes`
                  : ''}
              </span>
            </div>
          </div>

          {saveError && <p className="text-danger supporting m-0">{saveError}</p>}
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            className="border-line text-foreground hover:bg-hover h-8 rounded-lg border bg-transparent px-3 text-meta"
          >
            Cancel
          </Button>
          <Button
            onClick={onSave}
            disabled={!canSave}
            className="bg-cta hover:bg-ctahover h-8 rounded-lg px-3 text-meta text-white"
          >
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
